import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { interval, merge, shareReplay, Subject, takeUntil, tap, timer } from 'rxjs';
import { AccountingApiService } from '../../_services/accounting-service/accounting-api/accounting-api.service';
import { TransactionsStateModel } from './transactions.models';
import { AccountingSync, AccountingSyncStatus } from '../../_interfaces/accounting.interface';
import {
  ClearCategoriesSyncStatus,
  ClearContactsSyncStatus,
  ClearTransactionsState,
  ClearTransactionsSyncStatus,
  SetContactsSyncStatus,
  SetTransactionSyncStatus,
  SetTransactionSyncTimeout,
  StartPollingCategoriesSyncStatus,
  StartPollingContactSyncStatus,
  StartPollingTransactionSyncStatus,
  StopPollingAccountingSyncStatus,
} from './transactions.actions';
import { ToastService } from '../../_services/toast-service/toast.service';
import { SetTimedOutState } from '../id-verification/id-verification.actions';

type SyncConfig = {
  statusKey: 'transactionSyncStatus' | 'contactsSyncStatus' | 'categoriesSyncStatus';
  successMessage: string;
  failMessage: string;
  errorMessage: string;
};

@State<TransactionsStateModel>({
  name: 'transactions',
  defaults: {
    error: false,
    transactionSyncStatus: AccountingSyncStatus.New,
    contactsSyncStatus: AccountingSyncStatus.New,
    categoriesSyncStatus: AccountingSyncStatus.New,
    busySyncing: false,
    timeout: false,
  },
})
@Injectable()
export class TransactionsState {
  readonly accountingApiService = inject(AccountingApiService);
  readonly toastService = inject(ToastService);
  readonly store = inject(Store);

  private readonly POLL_INTERVAL = 2000; // 2 seconds
  public readonly stopPolling$ = new Subject<void>();

  private readonly TIME_OUT = 120 * 1000; // 2 minutes (120 seconds)
  private stopSignal$ = new Subject<void>();
  private syncCompleted = false; // Flag to prevent duplicate toasts

  private readonly SYNC_CONFIGS: Record<'transactions' | 'contacts' | 'categories', SyncConfig> = {
    transactions: {
      statusKey: 'transactionSyncStatus',
      successMessage: 'Transactions synced successfully.',
      failMessage: 'All selected transactions could not be synced.',
      errorMessage: 'Transaction syncing failed.',
    },
    contacts: {
      statusKey: 'contactsSyncStatus',
      successMessage: 'Contacts updated successfully.',
      failMessage: 'Contacts could not be updated.',
      errorMessage: 'Failed to refresh contact list.',
    },
    categories: {
      statusKey: 'categoriesSyncStatus',
      successMessage: 'Categories synced successfully.',
      failMessage: 'Categories could not be synced.',
      errorMessage: 'Category syncing failed.',
    },
  };

  /**
   * Generic method to query sync status for different entity types
   * @param ctx State context
   * @param config Configuration for the specific sync type
   */
  private querySyncStatus({ patchState }: StateContext<TransactionsStateModel>, config: SyncConfig) {
    const statusDone = AccountingSyncStatus.Done;
    const statusFail = AccountingSyncStatus.Fail;
    this.accountingApiService.getAccountingSyncStatus().subscribe({
      next: (res) => {
        patchState({ busySyncing: true });
        patchState({ [config.statusKey]: res.status } as Partial<TransactionsStateModel>);
        if (res.status === statusDone || res.status === statusFail) {
          patchState({ busySyncing: false });
          this.stopPolling$.next();
          this.stopSignal$.next();

          // Only show toast if this is the first completion (prevent duplicate toasts on slow connections)
          if (!this.syncCompleted) {
            this.syncCompleted = true;
            if (res.status === statusDone) {
              this.toastService.show(config.successMessage, 'iconoir-check');
            }
            if (res.status === statusFail) {
              this.toastService.show(config.failMessage, 'iconoir-xmark');
            }
          }
        }
      },
      error: () => {
        patchState({ busySyncing: false });
        this.stopPolling$.next();
        this.stopSignal$.next();
        // Only show error toast if sync hasn't already completed
        if (!this.syncCompleted) {
          this.syncCompleted = true;
          this.toastService.show(config.errorMessage, 'iconoir-xmark');
        }
      },
    });
  }

  queryTransactionSyncStatus(ctx: StateContext<TransactionsStateModel>) {
    this.querySyncStatus(ctx, this.SYNC_CONFIGS.transactions);
  }

  queryContactSyncStatus(ctx: StateContext<TransactionsStateModel>) {
    this.querySyncStatus(ctx, this.SYNC_CONFIGS.contacts);
  }

  queryCategoriesSyncStatus(ctx: StateContext<TransactionsStateModel>) {
    this.querySyncStatus(ctx, this.SYNC_CONFIGS.categories);
  }

  /**
   * Generic method to set sync status from API
   * @param patchState State patch function
   * @param statusKey Which status field to update ('transactionSyncStatus' or 'contactsSyncStatus')
   */
  private setSyncStatus(
    patchState: StateContext<TransactionsStateModel>['patchState'],
    statusKey: 'transactionSyncStatus' | 'contactsSyncStatus',
  ): void {
    this.accountingApiService
      .getAccountingSyncStatus()
      .pipe(
        tap({
          next: (res: AccountingSync) => {
            if (res) {
              patchState({ [statusKey]: res.status } as Partial<TransactionsStateModel>);
              // If sync is busy, make sure busySyncing is true so UI shows loading state
              if (res.status === AccountingSyncStatus.Busy) {
                patchState({ busySyncing: true });
              }
            }
          },
          error: () => {
            patchState({ [statusKey]: null, error: true } as Partial<TransactionsStateModel>);
          },
        }),
        shareReplay(1),
      )
      .subscribe();
  }

  @Action(SetTransactionSyncStatus)
  setTransactionSyncStatus({ patchState }: StateContext<TransactionsStateModel>) {
    this.setSyncStatus(patchState, 'transactionSyncStatus');
  }

  @Action(SetContactsSyncStatus)
  setContactsSyncStatus({ patchState }: StateContext<TransactionsStateModel>) {
    this.setSyncStatus(patchState, 'contactsSyncStatus');
  }

  /**
   * Generic method to start polling for sync status with timeout handling
   * @param ctx State context
   * @param queryFn Function to query the sync status
   */
  private startPolling(
    ctx: StateContext<TransactionsStateModel>,
    queryFn: (ctx: StateContext<TransactionsStateModel>) => void,
  ): void {
    ctx.patchState({ timeout: false });

    // Reset the sync completed flag for this new polling session
    this.syncCompleted = false;

    // Create a new stop signal for this polling session
    this.stopSignal$ = new Subject<void>();

    // Create timeout that will stop polling after TIME_OUT
    const timeout$ = timer(this.TIME_OUT).pipe(
      tap(() => {
        this.store.dispatch(new SetTransactionSyncTimeout());
        this.stopSignal$.next();
      }),
    );

    // Wait a brief moment before the initial query to allow the backend to update status to Busy
    // Without this delay, the backend might still return the cached "Done" status from the previous sync
    timer(500)
      .pipe(
        tap(() => {
          queryFn(ctx);
        }),
        takeUntil(this.stopSignal$),
      )
      .subscribe();

    // Poll at regular intervals until stopped or timed out
    // Start the interval immediately so polling continues after the initial query
    interval(this.POLL_INTERVAL)
      .pipe(takeUntil(merge(this.stopSignal$, timeout$)))
      .subscribe(() => {
        queryFn(ctx);
      });
  }

  @Action(StartPollingTransactionSyncStatus)
  startPollingTransactionSyncStatus(ctx: StateContext<TransactionsStateModel>) {
    this.startPolling(ctx, this.queryTransactionSyncStatus.bind(this));
  }

  @Action(StartPollingContactSyncStatus)
  startPollingContactsSyncStatus(ctx: StateContext<TransactionsStateModel>) {
    this.startPolling(ctx, this.queryContactSyncStatus.bind(this));
  }

  @Action(StartPollingCategoriesSyncStatus)
  startPollingCategoriesSyncStatus(ctx: StateContext<TransactionsStateModel>) {
    this.startPolling(ctx, this.queryCategoriesSyncStatus.bind(this));
  }

  @Action(StopPollingAccountingSyncStatus)
  stopPollingTransactionSyncStatus({ patchState }: StateContext<TransactionsStateModel>) {
    patchState({ busySyncing: false });
    this.stopPolling$.next();
    this.stopSignal$.next();
  }

  @Action(ClearTransactionsState)
  clearTransactionState({ patchState }: StateContext<TransactionsStateModel>) {
    patchState({
      error: false,
      busySyncing: false,
      timeout: false,
    });
  }

  @Action(ClearTransactionsSyncStatus)
  clearTransactionSyncStatus({ patchState }: StateContext<TransactionsStateModel>) {
    patchState({
      transactionSyncStatus: AccountingSyncStatus.New,
    });
  }

  @Action(ClearContactsSyncStatus)
  clearContactsSyncStatus({ patchState }: StateContext<TransactionsStateModel>) {
    patchState({
      contactsSyncStatus: AccountingSyncStatus.New,
    });
  }

  @Action(ClearCategoriesSyncStatus)
  clearCategoriesSyncStatus({ patchState }: StateContext<TransactionsStateModel>) {
    patchState({
      categoriesSyncStatus: AccountingSyncStatus.New,
    });
  }

  @Action(SetTimedOutState)
  setTimedOutState(ctx: StateContext<TransactionsStateModel>) {
    ctx.patchState({ timeout: true });
  }
}
